import { createContext, useContext, useState, useCallback } from 'react';

const translations = {
  en: {
    nav: {
      features: 'Features',
      pricing: 'Pricing',
      blog: 'Blog',
      signIn: 'Sign In',
      getStarted: 'Get Started',
    },
    hero: {
      badge: 'Precision Livestock Management',
      title: 'Run Your Farm Like a System — Not a',
      titleHighlight: 'Spreadsheet',
      subtitle: 'The operational OS for modern agriculture. Centralize herd data, automate feed logistics, and monitor growth with clinical precision.',
      cta: 'Start Free Trial',
      watchDemo: 'Watch Demo',
    },
    problem: {
      title: 'Farm Data Is Scattered — And It Costs You',
      subtitle: 'Traditional record-keeping creates blind spots. When weights aren\'t tracked and feed costs are estimated, your profit margin disappears into the gaps.',
      paperBlindness: 'Paper Blindness',
      paperBlindnessDesc: 'Manual logs are impossible to query and easy to lose.',
      invisibleWaste: 'Invisible Waste',
      invisibleWasteDesc: 'Poor feed conversion goes unnoticed without real-time tracking.',
    },
    solution: {
      title: 'One Operational System for Your Herd',
      subtitle: 'Stop reacting and start optimizing. AgriTrack brings every animal, every kilogram of feed, and every dollar spent into a single, unified lens of intelligence.',
      unifiedLedger: 'Unified Ledger',
      unifiedLedgerDesc: 'A single source of truth for breeding cycles, vaccinations, and health history across your entire operation.',
      growthIntelligence: 'Growth Intelligence',
      growthIntelligenceDesc: 'Automated weight gain analysis helps you identify top-performing genetics and adjust nutrition on the fly.',
      inventoryLogic: 'Inventory Logic',
      inventoryLogicDesc: 'Precision feed and medication tracking ensures you never run low and never overbuy high-cost supplies.',
    },
    features: {
      title: 'Built for Performance',
      animalTracking: 'Animal Tracking',
      animalTrackingDesc: 'Computer Vision-integrated life cycle management. From intake to final sale, track every vital metric with zero effort.',
      weightGrowth: 'Weight & Growth',
      weightGrowthDesc: 'Visualize daily average gain and benchmark against breed standards.',
      targetEfficiency: 'Target Efficiency',
      feedCost: 'Feed & Cost Control',
      feedCostDesc: 'Map ration composition to cost-per-head performance in real-time.',
      iotIntegration: 'IoT Integration',
      iotIntegrationDesc: 'Integrate directly with scales, weighing stations, and soil sensors to stream real-time data into your AgriTrack dashboard.',
    },
    pricing: {
      title: 'Start Free. No Setup Friction.',
      subtitle: 'Scalable precision for every size of operation.',
      recommended: 'Recommended',
      starter: 'Starter',
      starterPrice: '$0',
      starterPeriod: '/forever',
      starterFeature1: 'Up to 50 Head of Cattle',
      starterFeature2: 'Basic Health Tracking',
      starterFeature3: 'Inventory Ledger',
      pro: 'Pro',
      proPrice: '$49',
      proPeriod: '/mo',
      proFeature1: 'Unlimited Animals',
      proFeature2: 'Advanced Growth Analytics',
      proFeature3: 'Feed Ration Optimization',
      proFeature4: '3 Team Member Seats',
      enterprise: 'Enterprise',
      enterprisePrice: 'Custom',
      enterpriseDesc: 'For large-scale industrial operations and cooperatives requiring multi-site management.',
      enterpriseFeature1: 'API & IoT Integration',
      enterpriseFeature2: 'Custom Reporting',
      enterpriseFeature3: 'Dedicated Support',
      startFree: 'Start Free',
    },
    mobile: {
      title: 'Built for the Field',
      subtitle: 'AgriTrack isn\'t just for the office. Our web-first platform is fully responsive and optimized for mobile devices, ensuring you have the data you need while standing in the middle of a 500-acre paddock.',
      responsive: 'Responsive Web Platform',
      cloudSync: 'Instant Cloud Sync',
      offline: 'Optimized for Low Connectivity',
      offlineSync: 'Offline Sync',
    },
    testimonials: {
      title: 'Trusted by Forward-Thinking Farmers',
      subtitle: 'See how AgriTrack is transforming operations across the agricultural industry.',
    },
    faq: {
      title: 'Frequently Asked Questions',
      subtitle: 'Everything you need to know about AgriTrack.',
      q1: 'How quickly can I get started?',
      a1: 'You can set up your farm profile and start logging animals in under 10 minutes. Our guided onboarding walks you through importing existing data from spreadsheets.',
      q2: 'Is my data secure?',
      a2: 'Absolutely. We use bank-level encryption for all data in transit and at rest. Your farm data is never shared with third parties and you maintain full ownership.',
      q3: 'Can I use AgriTrack offline?',
      a3: 'Yes! Our mobile interface works offline and syncs automatically when connectivity is restored. Perfect for remote paddocks with spotty coverage.',
      q4: 'What kind of support do you offer?',
      a4: 'Starter users get access to our knowledge base and community forum. Pro users receive priority email support. Enterprise customers get dedicated account management and phone support.',
      q5: 'Can I import my existing data?',
      a5: 'Yes, we support importing from Excel, CSV, and most common farm management software. Our migration team can also assist with complex data transfers.',
    },
    cta: {
      title: 'Start Using AgriTrack Today — Free',
      subtitle: 'Join hundreds of precision farmers who have traded guesswork for data. Setup takes minutes.',
      button: 'Start Free',
      noCard: 'No credit card required. Free tier forever.',
    },
    footer: {
      product: 'Product',
      pricing: 'Pricing',
      contact: 'Contact',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      security: 'Security',
      copyright: '© 2024 AgriTrack. Precision Intelligence for the Modern Harvest.',
      samperlabs: 'A SamperLabs product',
    },
  },
  tr: {
    nav: {
      features: 'Özellikler',
      pricing: 'Fiyatlandırma',
      blog: 'Blog',
      signIn: 'Giriş Yap',
      getStarted: 'Başla',
    },
    hero: {
      badge: 'Hassas Çiftlik Yönetimi',
      title: 'Çiftliğinizi Sistem Gibi Yönetin — Değil Bir',
      titleHighlight: 'Elektronik Tablo',
      subtitle: 'Modern tarım için operasyonel işletim sistemi. Sürü verilerini merkezileştirin, yem lojistiğini otomatikleştirin ve büyümeyi klinik hassasiyetle izleyin.',
      cta: 'Ücretsiz Başla',
      watchDemo: 'Demo İzle',
    },
    problem: {
      title: 'Çiftlik Verileri Dağınık — Ve Size Maliyetli',
      subtitle: 'Geleneksel kayıt tutma kör noktalar yaratır. Tartılar izlenmediğinde ve yem maliyetleri tahmin edildiğinde, kar marjınız boşluklarda kaybolur.',
      paperBlindness: 'Kağıt Körlüğü',
      paperBlindnessDesc: 'Manuel kayıtlar sorgulanamaz ve kolayca kaybolur.',
      invisibleWaste: 'Görünmez İsraf',
      invisibleWasteDesc: 'Zayıf yem dönüşümü gerçek zamanlı takip olmadan fark edilmez.',
    },
    solution: {
      title: 'Sürünüz İçin Tek Operasyonel Sistem',
      subtitle: 'Tepki vermeyi bırakın ve optimize etmeye başlayın. AgriTrack her hayvanı, her kilogram yemi ve harcanan her lirayı tek, birleşik bir zeka merceğine getirir.',
      unifiedLedger: 'Birleşik Kayıt Defteri',
      unifiedLedgerDesc: 'Tüm operasyonunuz için üreme döngüleri, aşılar ve sağlık geçmişi için tek gerçek kaynağı.',
      growthIntelligence: 'Büyüme Zekası',
      growthIntelligenceDesc: 'Otomatik kilo artışı analizi, en iyi performans gösteren genetiği belirlemenize ve beslenmeyi anında ayarlamanıza yardımcı olur.',
      inventoryLogic: 'Envanter Mantığı',
      inventoryLogicDesc: 'Hassas yem ve ilaç takibi, asla azalmadan ve yüksek maliyetli malzemeleri asla fazla satın almadan emin olmanızı sağlar.',
    },
    features: {
      title: 'Performans İçin Tasarlandı',
      animalTracking: 'Hayvan Takibi',
      animalTrackingDesc: 'Bilgisayar Görüşü entegreli yaşam döngüsü yönetimi. Girişten son satışa kadar, sıfır çabayla her hayati metriği takip edin.',
      weightGrowth: 'Ağırlık ve Büyüme',
      weightGrowthDesc: 'Günlük ortalama kazancı görselleştirin ve ırk standartlarına göre kıyaslayın.',
      targetEfficiency: 'Hedef Verimlilik',
      feedCost: 'Yem ve Maliyet Kontrolü',
      feedCostDesc: 'Rasyon kompozisyonunu gerçek zamanlı olarak kafa başına maliyet performansına eşleyin.',
      iotIntegration: 'IoT Entegrasyonu',
      iotIntegrationDesc: 'Gerçek zamanlı verileri AgriTrack kontrol panelinize aktarmak için kantarlar, tartım istasyonları ve toprak sensörleriyle doğrudan entegre olun.',
    },
    pricing: {
      title: 'Ücretsiz Başla. Kurulum Sıkıntısı Yok.',
      subtitle: 'Her boyuttaki operasyon için ölçeklenebilir hassasiyet.',
      recommended: 'Önerilen',
      starter: 'Başlangıç',
      starterPrice: '₺0',
      starterPeriod: '/sonsuza dek',
      starterFeature1: '50 Başa Kadar Sığır',
      starterFeature2: 'Temel Sağlık Takibi',
      starterFeature3: 'Envanter Defteri',
      pro: 'Pro',
      proPrice: '₺1.499',
      proPeriod: '/ay',
      proFeature1: 'Sınırsız Hayvan',
      proFeature2: 'Gelişmiş Büyüme Analitiği',
      proFeature3: 'Yem Rasyonu Optimizasyonu',
      proFeature4: '3 Ekip Üyesi Koltuğu',
      enterprise: 'Kurumsal',
      enterprisePrice: 'Özel',
      enterpriseDesc: 'Çoklu site yönetimi gerektiren büyük ölçekli endüstriyel operasyonlar ve kooperatifler için.',
      enterpriseFeature1: 'API ve IoT Entegrasyonu',
      enterpriseFeature2: 'Özel Raporlama',
      enterpriseFeature3: 'Özel Destek',
      startFree: 'Ücretsiz Başla',
    },
    mobile: {
      title: 'Saha İçin Tasarlandı',
      subtitle: 'AgriTrack sadece ofis için değil. Web öncelikli platformumuz tamamen duyarlı ve mobil cihazlar için optimize edilmiştir, 500 dönümlük bir meranın ortasında dururken ihtiyacınız olan verilere sahip olmanızı sağlar.',
      responsive: 'Duyarlı Web Platformu',
      cloudSync: 'Anında Bulut Senkronizasyonu',
      offline: 'Düşük Bağlantı İçin Optimize',
      offlineSync: 'Çevrimdışı Senkronizasyon',
    },
    testimonials: {
      title: 'İleri Görüşlü Çiftçiler Tarafından Güvenilir',
      subtitle: 'AgriTrack\'in tarım sektöründe operasyonları nasıl dönüştürdüğünü görün.',
    },
    faq: {
      title: 'Sıkça Sorulan Sorular',
      subtitle: 'AgriTrack hakkında bilmeniz gereken her şey.',
      q1: 'Ne kadar çabuk başlayabilirim?',
      a1: 'Çiftlik profilinizi kurup 10 dakikadan kısa sürede hayvanları kaydetmeye başlayabilirsiniz. Rehberli başlangıcımız, elektronik tablolardan mevcut verileri içe aktarma konusunda size yol gösterir.',
      q2: 'Verilerim güvende mi?',
      a2: 'Kesinlikle. Transit halindeki ve duran tüm veriler için banka düzeyinde şifreleme kullanıyoruz. Çiftlik verileriniz asla üçüncü taraflarla paylaşılmaz ve tam mülkiyeti siz korursunuz.',
      q3: 'AgriTrack\'i çevrimdışı kullanabilir miyim?',
      a3: 'Evet! Mobil arayüzümüz çevrimdışı çalışır ve bağlantı yeniden sağlandığında otomatik olarak senkronize olur. Zayıf kapsama alanına sahip uzak meralar için mükemmel.',
      q4: 'Ne tür destek sunuyorsunuz?',
      a4: 'Başlangıç kullanıcıları bilgi tabanımıza ve topluluk forumumuza erişebilir. Pro kullanıcılar öncelikli e-posta desteği alır. Kurumsal müşteriler özel hesap yönetimi ve telefon desteği alır.',
      q5: 'Mevcut verilerimi içe aktarabilir miyim?',
      a5: 'Evet, Excel, CSV ve çoğu yaygın çiftlik yönetim yazılımından içe aktarmayı destekliyoruz. Taşıma ekibimiz karmaşık veri transferlerinde de yardımcı olabilir.',
    },
    cta: {
      title: 'Bugün AgriTrack Kullanmaya Başlayın — Ücretsiz',
      subtitle: 'Tahmini veriye dönüştüren yüzlerce hassas çiftçiye katılın. Kurulum dakikalar içinde tamamlanır.',
      button: 'Ücretsiz Başla',
      noCard: 'Kredi kartı gerekmez. Sonsuza kadar ücretsiz katman.',
    },
    footer: {
      product: 'Ürün',
      pricing: 'Fiyatlandırma',
      contact: 'İletişim',
      privacy: 'Gizlilik Politikası',
      terms: 'Hizmet Şartları',
      security: 'Güvenlik',
      copyright: '© 2024 AgriTrack. Modern Hasat için Hassas Zeka.',
      samperlabs: 'Bir SamperLabs ürünü',
    },
  },
};

const I18nContext = createContext();

export const I18nProvider = ({ children }) => {
  const [locale, setLocale] = useState('tr'); // Default to Turkish as per requirements

  const t = useCallback((key) => {
    const keys = key.split('.');
    let value = translations[locale];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  }, [locale]);

  const toggleLocale = useCallback(() => {
    setLocale(prev => prev === 'en' ? 'tr' : 'en');
  }, []);

  return (
    <I18nContext.Provider value={{ locale, setLocale, t, toggleLocale }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};

export { translations };
